/**
 * Re-apply and Dismiss for one parked payment event (TMMPDM-0071, TMM-B4). One ConfirmDialog for both:
 * each asks for a required note of 3–500 characters, and "Dismiss instead" switches a refused or
 * unavailable re-apply to the Dismiss dialog with the note kept (the parent changes `mode`; this
 * component stays mounted, so the note does too).
 *
 * Every refusal is shown inside the open dialog, once, in catalog words (TMM-B3), and none changes the
 * event: 409 `PARKED_EVENT_STILL_REFUSED` (with the new reason in words), 409 `PARKED_EVENT_UNAVAILABLE`
 * (with Dismiss instead), 409 `PARKED_EVENT_RESOLVED` and 404 `PARKED_EVENT_NOT_FOUND` (someone else got
 * there first), 400 `VALIDATION_FAILED` (the note field shows its rule). After any other failure the
 * parent re-reads the list: the event may have moved on, and the list is where that shows.
 *
 * TMMPDM-0072: a still-refused re-apply leaves the row as it was (tmm-api keeps the stored reason), so
 * the new reason goes up to the parent (`onStillRefused`), which shows it under the row's Reason. After
 * RESOLVED or NOT_FOUND there is nothing left to confirm: the confirm is disabled and dismiss reads
 * Close. 503 `PAY_API_UNAVAILABLE` on a re-apply has its own words (the event can't be fetched).
 * TMMPDM-0076 (tmm-api 0123): 503 `PARKED_EVENT_BUSY` (the poller or another re-apply holds the event;
 * nothing changed) says to try again in a moment. The dialog stays open with Confirm enabled for the
 * retry, and nothing is re-read.
 * TMMPDM-0080 (tmm-api 0126): a busy Dismiss answers 503 `PARKED_EVENT_BUSY` too (as above). A 500 on either
 * action takes the generic failure path, and the list is re-read.
 */
import { useRef, useState } from 'react';
import { Box, Button, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, useNotify } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isValidationFailed, parkedEventRefusal } from '@/api/errors';
import { PARKED_REASON_CODES } from '@/api/tmm/types';
import { refusalReason } from './parkedEventCopy';

export type ParkedAction = 'reapply' | 'dismiss';

/** `ParkedEventNoteRequest.note`: 3–500 characters (TMMAPI-0121), trimmed. */
const NOTE_MIN = 3;
const NOTE_MAX = 500;

interface ParkedEventDialogProps {
  open: boolean;
  seq: number;
  mode: ParkedAction;
  onClose: () => void;
  /** The event was resolved by this dialog: the parent closes it and re-reads. */
  onResolved: () => void;
  /** A re-apply was refused again: the new reason code (`''` when the server sent none). Nothing changed. */
  onStillRefused: (reasonCode: string) => void;
  /** The server's answer may differ from the list on screen: the parent re-reads. */
  onChanged: () => void;
  /** Switch to the Dismiss dialog, keeping the note. */
  onDismissInstead: () => void;
}

export function ParkedEventDialog({
  open,
  seq,
  mode,
  onClose,
  onResolved,
  onStillRefused,
  onChanged,
  onDismissInstead,
}: ParkedEventDialogProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const notify = useNotify();
  const [note, setNote] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<{ text: string; unavailable: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  /** Resolved by someone else, or no longer parked: nothing is left to confirm. */
  const [gone, setGone] = useState(false);
  const inFlight = useRef(false);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const reapply = mode === 'reapply';
  const counter = t('parkedEvents.dialog.counter', '{{count, number}}/{{max, number}}', {
    count: note.length,
    max: NOTE_MAX,
  });

  const rejectNote = () => {
    setFieldError(t('parkedEvents.dialog.noteRule', 'Write a note of 3 to 500 characters.'));
    noteRef.current?.focus();
  };

  /** The words for a failed write, and whether Dismiss instead is the way out. */
  const failureCopy = (failure: unknown) => {
    const reason = parkedEventRefusal(failure);
    if (reason !== null && (PARKED_REASON_CODES as readonly string[]).includes(reason)) {
      return {
        text: t('parkedEvents.reapply.stillRefused', 'Still refused: {{reason}}.', {
          reason: refusalReason(reason, t, i18n.language),
        }),
        unavailable: false,
      };
    }
    if (reapply && failure instanceof ApiError && String(failure.code) === 'PAY_API_UNAVAILABLE') {
      return {
        text: t(
          'parkedEvents.reapply.payApiUnavailable',
          "pay-api isn't available right now, so the event can't be fetched. Try again later.",
        ),
        unavailable: false,
      };
    }
    const fallback = reapply
      ? t('parkedEvents.reapply.failed', "Couldn't re-apply the event.")
      : t('parkedEvents.dismiss.failed', "Couldn't dismiss the event.");
    return {
      text: errorCopy(failure, t, fallback),
      unavailable:
        reapply &&
        failure instanceof ApiError &&
        String(failure.code) === 'PARKED_EVENT_UNAVAILABLE',
    };
  };

  const submit = async () => {
    if (inFlight.current || gone) return;
    const trimmed = note.trim();
    if (trimmed.length < NOTE_MIN || trimmed.length > NOTE_MAX) {
      rejectNote();
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setFieldError(null);
    setError(null);
    try {
      if (reapply) await adminApi.reapplyParkedEvent(seq, { note: trimmed });
      else await adminApi.dismissParkedEvent(seq, { note: trimmed });
      notify.success(
        reapply
          ? t('parkedEvents.reapply.done', 'Event re-applied.')
          : t('parkedEvents.dismiss.done', 'Event dismissed.'),
      );
      onResolved();
    } catch (failure: unknown) {
      if (isValidationFailed(failure)) {
        rejectNote();
        return;
      }
      const refusedReason = parkedEventRefusal(failure);
      if (refusedReason !== null) onStillRefused(refusedReason);
      if (
        failure instanceof ApiError &&
        ['PARKED_EVENT_RESOLVED', 'PARKED_EVENT_NOT_FOUND'].includes(String(failure.code))
      ) {
        setGone(true);
      }
      setError(failureCopy(failure));
      // A busy event is unchanged, and the retry is this dialog's Confirm: nothing to re-read.
      if (!(failure instanceof ApiError && String(failure.code) === 'PARKED_EVENT_BUSY'))
        onChanged();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      title={
        reapply
          ? t('parkedEvents.reapply.title', 'Re-apply event {{seq, number}}?', { seq })
          : t('parkedEvents.dismiss.title', 'Dismiss event {{seq, number}}?', { seq })
      }
      consequence={
        <Stack spacing={2}>
          <Box component="span">
            {reapply
              ? t(
                  'parkedEvents.reapply.consequence',
                  'tmm-api fetches it from pay-api and applies it once.',
                )
              : t(
                  'parkedEvents.dismiss.consequence',
                  'Nothing is applied. Use this when it was handled elsewhere, or when the member was erased.',
                )}
          </Box>
          <TextField
            label={t('parkedEvents.dialog.note', 'Note')}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
              setFieldError(null);
            }}
            error={fieldError !== null}
            helperText={fieldError === null ? counter : `${fieldError} ${counter}`}
            inputRef={noteRef}
            slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
            required
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      }
      confirmLabel={
        reapply
          ? t('parkedEvents.reapply.confirm', 'Re-apply')
          : t('parkedEvents.dismiss.confirm', 'Dismiss')
      }
      {...(gone ? { cancelLabel: t('common.close', 'Close') } : {})}
      confirmDisabled={gone}
      busy={busy}
      error={
        error ? (
          <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
            <Box component="span">{error.text}</Box>
            {error.unavailable ? (
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => {
                  // The button is leaving: keep focus inside the dialog, on the note that stays.
                  setError(null);
                  setFieldError(null);
                  noteRef.current?.focus();
                  onDismissInstead();
                }}
              >
                {t('parkedEvents.dismissInstead', 'Dismiss instead')}
              </Button>
            ) : null}
          </Stack>
        ) : undefined
      }
      onConfirm={() => void submit()}
      onCancel={onClose}
    />
  );
}
